import React, { useState } from 'react';
import { Sparkles, Zap, Shield, Heart, ArrowRight, Check, BookOpen } from 'lucide-react';
import { markTutorialSeen } from '../services/firestore.js';

export default function OnboardingTutorial({ user, onClose, onNavigate }) {
    const [step, setStep] = useState(0);

    const handleNext = () => setStep(prev => prev + 1);

    const handleClose = async () => {
        if (user) {
            await markTutorialSeen(user.uid);
        }
        onClose();
    };

    const handleOpenGuidelines = () => {
        handleClose();
        if (onNavigate) onNavigate('guidelines');
    };

    const steps = [
        {
            icon: React.createElement(Sparkles, { size: 48, color: '#eab308' }),
            image: "/images/mascots/SparkHi.png", // Mascot Integration
            title: "Welcome to Spark Inspo! ✨",
            content: "You've joined a creative community dedicated to turning boredom into inspiration. Here, every idea starts with a Spark."
        },
        {
            icon: React.createElement(Zap, { size: 48, color: '#8b5cf6' }),
            image: "/images/mascots/MuseGlow.png",
            title: "Create Your First Spark ⚡",
            content: "Found something cool? Click the purple 'Spark' button to share it. Whether it's a quote, functionality, or design—capture it!"
        },
        {
            icon: React.createElement(Heart, { size: 48, color: '#ec4899' }),
            image: "/images/mascots/SparkLove.png",
            title: "Spread the Love 💖",
            content: "See something you like? React with custom emotes or give it a Like. Support other creators and build your streak!"
        },
        {
            icon: React.createElement(Shield, { size: 48, color: '#10b981' }),
            image: "/images/mascots/BitBot.png",
            title: "Keep it Safe 🛡️",
            content: "We value a positive environment. Please respect our Community Guidelines and help us keep this space inspiring for everyone."
        }
    ];

    const currentStep = steps[step];
    const isLastStep = step === steps.length - 1;

    return React.createElement('div', {
        style: {
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)',
            backdropFilter: 'blur(5px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease-out'
        }
    },
        React.createElement('div', {
            className: 'glass-panel animate-scale-in',
            style: {
                width: '450px',
                maxWidth: '90%',
                background: '#18181b',
                borderRadius: '24px',
                padding: '40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(255,255,255,0.1)'
            }
        },
            // Step Indicators
            React.createElement('div', { style: { display: 'flex', gap: '8px', marginBottom: '30px' } },
                steps.map((_, idx) => React.createElement('div', {
                    key: idx,
                    style: {
                        width: '8px', height: '8px', borderRadius: '50%',
                        background: idx === step ? '#fff' : 'rgba(255,255,255,0.2)',
                        transition: 'all 0.3s ease'
                    }
                }))
            ),

            // Icon or Mascot Image
            currentStep.image ? (
                <img
                    src={currentStep.image}
                    alt="Mascot"
                    style={{
                        maxHeight: '150px',
                        maxWidth: '100%',
                        marginBottom: '20px',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))'
                    }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        // If image fails, show fallback icon if available
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                    }}
                />
            ) : (
                React.createElement('div', {
                    style: {
                        background: 'rgba(255,255,255,0.05)',
                        padding: '20px',
                        borderRadius: '50%',
                        marginBottom: '20px',
                        animation: 'float 3s ease-in-out infinite'
                    }
                }, currentStep.icon)
            ),

            // Title
            React.createElement('h2', {
                style: {
                    fontSize: '24px',
                    marginBottom: '15px',
                    background: 'linear-gradient(to right, #fff, #a1a1aa)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }
            }, currentStep.title),

            // Content
            React.createElement('p', {
                style: {
                    fontSize: '16px',
                    lineHeight: '1.6',
                    color: '#a1a1aa',
                    marginBottom: '30px'
                }
            }, currentStep.content),

            // Special Link for Last Step
            isLastStep && React.createElement('button', {
                onClick: handleOpenGuidelines,
                className: 'btn-ghost',
                style: {
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#10b981',
                    fontSize: '14px'
                }
            }, React.createElement(BookOpen, { size: 16 }), "Read Community Guidelines"),

            // Buttons
            React.createElement('button', {
                onClick: isLastStep ? handleClose : handleNext,
                className: 'btn-primary',
                style: {
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    background: isLastStep ? '#10b981' : 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                    marginTop: 'auto'
                }
            },
                isLastStep ? "Get Started" : "Next Tip",
                isLastStep ? React.createElement(Check, { size: 20 }) : React.createElement(ArrowRight, { size: 20 })
            )
        )
    );
}
